import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(0);
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard", { replace: true });
      return;
    }

    let cancelled = false;
    const maxAttempts = 8;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="pt-32 pb-16 max-w-xl mx-auto px-4 sm:px-6 text-center" data-testid="payment-page">
      <SEO title="Payment status" description="Confirming your Serverlistr Featured listing." path="/payment/success" noindex />
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-blurple animate-spin mx-auto" />
          <h1 className="mt-6 font-heading text-2xl font-bold">Confirming payment...</h1>
          <p className="mt-2 text-sm text-zinc-400">Hang tight, this only takes a second.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-neon-green mx-auto" />
          <h1 className="mt-6 font-heading text-3xl font-black">Payment confirmed!</h1>
          <p className="mt-2 text-sm text-zinc-400 font-mono">${amount.toFixed(2)} USD · your server is now featured.</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-8 h-11 px-6 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold"
            data-testid="go-dashboard-btn"
          >
            Back to dashboard
          </button>
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-14 h-14 text-red-400 mx-auto" />
          <h1 className="mt-6 font-heading text-2xl font-bold">Something went off-track</h1>
          <p className="mt-2 text-sm text-zinc-400">Please try again from your dashboard.</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-8 h-11 px-6 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold"
          >
            Back to dashboard
          </button>
        </>
      )}
    </div>
  );
}
